// Shared layout for service hub pages — receives a ServiceMeta and renders full page.
import { SITE_CONTENT_REVIEWED } from "@/lib/site-content-meta";
import { Link } from "@tanstack/react-router";
import type { ServiceMeta } from "@/data/services";
import { SERVICES } from "@/data/services";
import { locations } from "@/data/locations";
import {
  AeoAnswerBox, AtomicStats, EntityPanel, CitationList, HowToSteps,
  FaqAccordion, PriceMatrix, ReviewBadge, ContactCTA, TrustBar,
  Breadcrumbs, ServiceCoverageLinks,
} from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedCross } from "@/components/RelatedCross";
import { LazyHydrate } from "@/components/LazyHydrate";
import { relatedForService } from "@/lib/related";
import { consolidateGraph } from "@/lib/schema-graph";
import { ServiceJumpNav } from "@/components/service/ServiceJumpNav";
import { ServiceWhyChoose } from "@/components/service/ServiceWhyChoose";
import { ServiceComparisonTable } from "@/components/service/ServiceComparisonTable";
import { ServiceStickyActionBar } from "@/components/service/ServiceStickyActionBar";


export function ServiceHub({ s, intro, longForm }: { s: ServiceMeta; intro: string; longForm: React.ReactNode }) {
  const branches = locations.filter((l) => l.type === "headquarters" || l.type === "branch");
  const top20 = locations.filter((l) => l.type === "bkk-district").slice(0, 20);

  return (
    <main className="min-h-screen bg-gradient-to-b from-navy-deep via-navy to-navy-deep text-white">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10 px-4 py-12 sm:px-6 sm:py-16">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,hsl(var(--gold)/0.18),transparent_60%),radial-gradient(circle_at_80%_100%,hsl(var(--gold)/0.12),transparent_55%)]" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" aria-hidden="true" />
        <div className="relative mx-auto max-w-5xl space-y-5">
          <Breadcrumbs items={[{ label: "หน้าแรก", to: "/" }, { label: "บริการ", to: "/services" }, { label: s.nameTh }]} />
          <div className="flex flex-wrap items-center gap-2">
            <ReviewBadge />
            <span className="rounded-full bg-gold/20 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-gold">บริการเรือธง</span>
            <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white/85">ตั้งแต่ปี 2559</span>
          </div>
          <h1 className="font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">{s.nameTh}</h1>
          <p className="max-w-3xl text-base text-white/80 sm:text-lg">{s.taglineTh}</p>
          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="rounded-lg border border-gold/30 bg-gold/10 px-2.5 py-1 font-bold text-gold">เริ่ม ฿{s.priceFrom.toLocaleString()}</span>
            <span className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1 font-semibold text-white/90">เสร็จภายใน {s.turnaround}</span>
            <span className="rounded-lg border border-white/15 bg-white/5 px-2.5 py-1 font-semibold text-white/90">{s.languagesCount}+ ภาษา</span>
          </div>
          <TrustBar />
          <ContactCTA />
        </div>
      </section>

      <ServiceJumpNav />

      <div id="overview" className="mx-auto max-w-5xl space-y-12 px-4 py-12 scroll-mt-20 sm:px-6 sm:py-16">

        {/* AEO Quick Answer */}
        <AeoAnswerBox question={`${s.nameTh} คืออะไร?`} answer={intro} />

        {/* Atomic Stats */}
        <AtomicStats stats={s.atomicStats} />

        {/* Why Choose NYC — 6 reasons bento */}
        <ServiceWhyChoose />


        {/* Quotable AEO callout — designed to be lifted verbatim by AI Overviews / Copilot */}
        <section className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/[0.12] via-gold/[0.04] to-transparent p-6 sm:p-8">
          <div className="text-[10px] font-bold uppercase tracking-widest text-gold/90">
            Quotable · AI Overview ready
          </div>
          <blockquote className="aeo-speakable mt-3 font-display text-lg italic leading-relaxed text-white sm:text-xl">
            “NYC Legal &amp; Notary Service ให้บริการ{s.nameTh}ในประเทศไทยมาตั้งแต่ปี 2559
            ภายใต้การกำกับของกระทรวงการต่างประเทศ (DBD 0435567000061)
            ทีมทนาย Notary Public 6 ท่าน รับรองคุณภาพระดับ {s.languagesCount}+ ภาษา
            ราคาเริ่ม ฿{s.priceFrom.toLocaleString()} เสร็จภายใน{s.turnaround}
            ทีมทนายผู้ทำคำรับรอง 6 ท่าน”
          </blockquote>
          <div className="mt-3 text-xs text-white/80">
            — Source: NYC Legal &amp; Notary Service · ตรวจทานล่าสุด {SITE_CONTENT_REVIEWED}
          </div>
        </section>

        {/* Long-form prose (passed in per-service) */}
        <article className="prose prose-invert prose-headings:text-white prose-strong:text-gold prose-a:text-gold max-w-none space-y-4 text-white/85">
          {longForm}
        </article>

        {/* Documents */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">ประเภทเอกสารที่ให้บริการ</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {s.documentsTh.map((d) => (
              <li key={d} className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/85">
                <span className="mt-0.5 text-gold">✓</span> {d}
              </li>
            ))}
          </ul>
        </section>

        {/* Purposes */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">นำไปใช้ทำอะไรได้บ้าง</h2>
          <ul className="grid gap-2 sm:grid-cols-2">
            {s.purposesTh.map((p) => (
              <li key={p} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/85">→ {p}</li>
            ))}
          </ul>
        </section>

        {/* HowTo */}
        <section id="howto" className="scroll-mt-20">
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">ขั้นตอนการดำเนินการ</h2>
          <HowToSteps steps={s.howTo} />
        </section>

        {/* Pricing */}
        <section id="pricing" className="scroll-mt-20">
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">ค่าบริการ (โดยประมาณ)</h2>
          <PriceMatrix rows={s.prices} />
          <p className="mt-3 text-xs text-white/80">* ราคาอาจปรับเปลี่ยนตามปริมาณงานและความเร่งด่วน · ส่งเอกสารให้ทีมงานประเมินฟรีทาง LINE @nycli</p>
        </section>

        {/* Comparison vs generic providers */}
        <ServiceComparisonTable />

        {/* Coverage */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">พื้นที่ให้บริการ</h2>
          <p className="mb-3 text-sm text-white/75">ครอบคลุม 50 เขตกรุงเทพมหานคร 77 จังหวัด สำนักงาน 4 สาขา และตัวแทนทั่วประเทศ</p>
          <h3 className="mb-2 mt-4 text-sm font-bold text-gold">สำนักงานสาขา</h3>
          <ServiceCoverageLinks locations={branches.map((l) => ({ slug: l.slug, nameTh: l.nameTh }))} />
          <h3 className="mb-2 mt-4 text-sm font-bold text-gold">20 เขตยอดนิยมในกรุงเทพฯ</h3>
          <ServiceCoverageLinks locations={top20.map((l) => ({ slug: l.slug, nameTh: l.nameTh }))} />
          <div className="mt-4">
            <Link to="/locations" className="inline-flex items-center gap-1 text-sm font-bold text-gold underline-offset-4 hover:underline">ดูพื้นที่ทั้งหมด 127 จุด →</Link>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="scroll-mt-20">
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">คำถามที่พบบ่อย (FAQ)</h2>
          <FaqAccordion items={s.faqs} />
        </section>


        {/* Entity Panel */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">ข้อมูลบริษัท · Entity Panel</h2>
          <EntityPanel
            rows={[
              { label: "ชื่อบริษัท", value: "NYC Legal & Notary Service Co., Ltd." },
              { label: "เลขทะเบียน DBD", value: "0435567000061" },
              { label: "ก่อตั้ง", value: "19 มกราคม 2559" },
              { label: "ทนาย Notary Public", value: "6 ท่าน · สภาทนายความฯ" },
              { label: "ภาษา", value: `${s.languagesCount}+ ภาษา` },
              { label: "ราคาเริ่มต้น", value: `฿${s.priceFrom.toLocaleString()}` },
              { label: "เวลาดำเนินการ", value: s.turnaround },
              { label: "พื้นที่ให้บริการ", value: s.coverage },
              { label: "ทนายผู้ทำคำรับรอง", value: "6 ท่าน" },
            ]}
          />
        </section>

        {/* Citations */}
        <CitationList sources={s.citations} />

        {/* Related Services */}
        <section>
          <h2 className="mb-4 text-xl font-bold text-white sm:text-2xl">บริการที่เกี่ยวข้อง</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {s.relatedServiceIds.map((id) => {
              const r = SERVICES[id];
              return (
                <li key={id}>
                  <Link to="/services/$service" params={{ service: r.slug }} className="block rounded-2xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-gold/40 hover:bg-white/[0.07]">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gold/80">บริการ</div>
                    <div className="mt-1 font-bold text-white">{r.nameTh}</div>
                    <div className="mt-1 text-xs text-white/80">{r.shortTh}</div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>

      </div>

      {/* Cross-section internal linking graph (lazy-hydrated — below the fold) */}
      <LazyHydrate minHeight={500} rootMargin="500px">
        <RelatedCross
          bundle={relatedForService(s.id)}
          theme="dark"
          heading="สำรวจเพิ่มเติม"
          sub="ลิงก์เชื่อมระหว่างบริการ บทความความรู้ และอุตสาหกรรมที่ใช้บริการนี้บ่อยที่สุด"
        />
      </LazyHydrate>

      {/* Trust Layer — full-bleed light section */}
      <TrustLayer />

      {/* Final CTA */}
      <div id="contact" className="mx-auto max-w-5xl px-4 pb-16 scroll-mt-20 sm:px-6">
        <section className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/15 via-white/5 to-transparent p-6 sm:p-8">
          <h2 className="text-xl font-extrabold sm:text-2xl">พร้อมเริ่มต้นใช้บริการแล้วใช่ไหม?</h2>
          <p className="mt-2 text-sm text-white/80">ส่งเอกสารให้เราประเมินฟรีภายใน 15 นาที — ทีมงานพร้อมตอบทุกช่องทางตลอดเวลาทำการ</p>
          <div className="mt-5"><ContactCTA /></div>
        </section>
      </div>

      {/* Mobile sticky action bar */}
      <ServiceStickyActionBar serviceName={s.nameTh} />
      {/* Spacer so sticky bar doesn't overlap footer content on mobile */}
      <div className="h-20 sm:hidden" aria-hidden="true" />
    </main>
  );
}


// Last content review — bumped manually whenever combo-content or service
// data is touched. Surfaces as `dateModified` for E-E-A-T freshness signals.
export { SITE_CONTENT_REVIEWED } from "@/lib/site-content-meta";

// JSON-LD builder shared by service hub routes
export function buildServiceJsonLd(s: ServiceMeta, url: string) {
  const SITE = "https://nyclanguageinstitute.com";
  const scripts = [
    {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: s.metaTitle,
        description: s.metaDesc,
        inLanguage: "th-TH",
        isPartOf: { "@id": `${SITE}#website` },
        about: { "@id": `${url}#service` },
        primaryImageOfPage: { "@type": "ImageObject", url: `${SITE}/og-default.webp` },
        datePublished: "2024-01-01",
        dateModified: SITE_CONTENT_REVIEWED,
        reviewedBy: { "@id": `${SITE}/about/team#org` },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [".aeo-speakable", "h1", "h2"],
        },
      }),
    },
    {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${url}#service`,
        name: s.nameTh,
        alternateName: s.nameEn,
        description: s.metaDesc,
        url,
        provider: { "@id": `${SITE}#org` },
        areaServed: { "@type": "Country", name: "Thailand" },
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "THB",
          lowPrice: s.priceFrom,
          highPrice: s.priceTo,
          offerCount: "182",
          availability: "https://schema.org/InStock",
          seller: { "@id": `${SITE}#org` },
          eligibleRegion: { "@type": "Country", name: "Thailand" },
        },
        termsOfService: `Turnaround: ${s.turnaround}`,
        serviceType: s.nameEn,
        category: "LegalService",
      }),
    },
    {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: s.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    },
    {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        "@id": `${url}#howto`,
        name: `ขั้นตอน ${s.nameTh}`,
        step: s.howTo.map((st, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: st.title,
          text: st.text,
        })),
      }),
    },
    {
      type: "application/ld+json" as const,
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${url}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE },
          { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
          { "@type": "ListItem", position: 3, name: s.nameTh, item: url },
        ],
      }),
    },
    // Round 11 — ClaimReview removed (Google policy: marketing pages cannot
    // emit ClaimReview; reserved for IFCN-signatory fact-checking orgs).
  ];
  return [consolidateGraph(scripts)];
}

